import React, { useState, useEffect } from "react";
import { ref, update } from "firebase/database";
import { db } from "../firebase";
import useFile from "../hooks/useFile";

const EditTeamPlayer = ({ gameId, teamId, playerId, player, players, onClose, onSuccess }) => {
  const { uploadImage, uploading, clearError, deleteFile } = useFile();
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    image: "",
    fileId: null,
    isCaptain: false,
  });

  useEffect(() => {
    if (player) {
      setFormData({
        name: player.name || "",
        image: player.image || "",
        fileId: player.fileId || null,
        isCaptain: player.isCaptain || false,
      });
      if (player.image) {
        setPreview(player.image);
      }
    }
  }, [player]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);

    // Delete old file if exists
    if (formData.fileId) {
      await deleteFile(formData.fileId);
    }

    // Upload new file
    clearError();
    const result = await uploadImage(selectedFile, {
      maxSize: 500 * 1024,
      allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    });

    if (result.success) {
      setFormData((prev) => ({
        ...prev,
        image: result.fileUrl,
        fileId: result.fileId,
      }));
      setError("");
    } else {
      setError(result.error || "Failed to upload image");
      setPreview(formData.image || null);
    }
  };

  const handleRemoveImage = async () => {
    if (formData.fileId) {
      setDeleting(true);
      await deleteFile(formData.fileId);
      setDeleting(false);
    }
    setPreview(null);
    setFormData((prev) => ({
      ...prev,
      image: "",
      fileId: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Player name is required");
      return;
    }

    if (!playerId) {
      setError("Player ID missing");
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Update player details
      await update(ref(db, `games/${gameId}/players/${playerId}`), {
        name: formData.name.trim(),
        image: formData.image,
        fileId: formData.fileId,
        isCaptain: formData.isCaptain,
        updatedAt: Date.now(),
      });

      // If captain changed, update team captain reference
      if (formData.isCaptain) {
        const updates = {};
        
        // Remove captain flag from other players in the team
        players.forEach((p) => {
          if (p.id !== playerId && p.isCaptain) {
            updates[`games/${gameId}/players/${p.id}/isCaptain`] = false;
          }
        });

        // Set new captain on team
        updates[`games/${gameId}/teams/${teamId}/captainPlayerId`] = playerId;
        updates[`games/${gameId}/teams/${teamId}/captainPlayerName`] = formData.name.trim();

        await update(ref(db), updates);
      }

      setSuccess(true);
      if (onSuccess) onSuccess();

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Error updating player:", err);
      setError("Failed to update player: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  
  const styles = {
    container: {
      background: isDark ? "#1e1e2f" : "white",
      borderRadius: "16px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
      overflow: "hidden",
      maxWidth: "500px",
      margin: "0 auto",
    },
    header: {
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      padding: "24px",
      color: "white",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    headerTitle: {
      margin: 0,
      fontSize: "1.5rem",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    closeButton: {
      background: "rgba(255,255,255,0.2)",
      border: "none",
      borderRadius: "50%",
      width: "36px",
      height: "36px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all 0.2s",
      color: "white",
      fontSize: "1.2rem",
    },
    body: {
      padding: "24px",
      background: isDark ? "#1e1e2f" : "white",
    },
    photoSection: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginBottom: "24px",
    },
    photoContainer: {
      position: "relative",
      marginBottom: "12px",
    },
    photo: {
      width: "120px",
      height: "120px",
      borderRadius: "50%",
      objectFit: "cover",
      border: "4px solid #10b981",
      boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
    },
    photoPlaceholder: {
      width: "120px",
      height: "120px",
      borderRadius: "50%",
      background: document.documentElement.getAttribute('data-theme') === 'dark' 
        ? "linear-gradient(135deg, #2d2d44 0%, #1e1e2f 100%)" 
        : "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      border: document.documentElement.getAttribute('data-theme') === 'dark' 
        ? "3px dashed #4a4a6a" 
        : "3px dashed #d1d5db",
      cursor: "pointer",
    },
    removeButton: {
      position: "absolute",
      bottom: "0",
      right: "0",
      background: "#ef4444",
      color: "white",
      border: "none",
      borderRadius: "50%",
      width: "32px",
      height: "32px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 8px rgba(239,68,68,0.4)",
    },
    uploadButton: {
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      color: "white",
      border: "none",
      borderRadius: "20px",
      padding: "8px 20px",
      fontSize: "0.9rem",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "600",
      color: isDark ? "#e0e0e0" : "#374151",
      fontSize: "0.95rem",
    },
    required: {
      color: "#ef4444",
      marginLeft: "4px",
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      border: isDark ? "2px solid #4a4a6a" : "2px solid #e5e7eb",
      borderRadius: "10px",
      fontSize: "1rem",
      transition: "all 0.2s",
      outline: "none",
      boxSizing: "border-box",
      background: isDark ? "#2d2d44" : "white",
      color: isDark ? "#e0e0e0" : "#212529",
    },
    switchContainer: {
      display: "flex",
      alignItems: "center",
      padding: "16px",
      background: isDark ? "#3d3520" : "#fefce8",
      borderRadius: "12px",
      border: isDark ? "2px solid #6b5a1e" : "2px solid #fde047",
    },
    switchLabel: {
      marginLeft: "12px",
      fontWeight: "600",
      color: isDark ? "#fde047" : "#854d0e",
    },
    switchNote: {
      fontSize: "0.85rem",
      color: isDark ? "#d4b83a" : "#a16207",
      marginTop: "4px",
    },
    submitButton: {
      width: "100%",
      padding: "14px",
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontSize: "1rem",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "all 0.2s",
    },
    successMessage: {
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      color: "white",
      padding: "16px",
      borderRadius: "10px",
      textAlign: "center",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
    },
    errorMessage: {
      background: "#fef2f2",
      color: "#dc2626",
      padding: "12px 16px",
      borderRadius: "10px",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.headerTitle}>
          <i className="bi bi-person-circle"></i>
          Edit Player
        </h4>
        <button
          style={styles.closeButton}
          onClick={onClose}
          onMouseOver={(e) => (e.target.style.background = "rgba(255,255,255,0.3)")}
          onMouseOut={(e) => (e.target.style.background = "rgba(255,255,255,0.2)")}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <div style={styles.body}>
        {success && (
          <div style={styles.successMessage}>
            <i className="bi bi-check-circle-fill fs-5"></i>
            <span>Player updated successfully!</span>
          </div>
        )}

        {error && (
          <div style={styles.errorMessage}>
            <i className="bi bi-exclamation-circle-fill"></i>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Photo Section */}
          <div style={styles.photoSection}>
            <div style={styles.photoContainer}>
              {preview ? (
                <>
                  <img src={preview} alt="Player" style={styles.photo} />
                  <button
                    type="button"
                    style={styles.removeButton}
                    onClick={handleRemoveImage}
                    disabled={deleting}
                    title="Remove photo"
                  >
                    {deleting ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <i className="bi bi-x-lg"></i>
                    )}
                  </button>
                  {uploading && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "rgba(0,0,0,0.7)",
                        color: "white",
                        padding: "8px 16px",
                        borderRadius: "20px",
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </div>
                  )}
                </>
              ) : (
                <label style={styles.photoPlaceholder}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    disabled={uploading}
                  />
                  {uploading ? (
                    <span className="spinner-border text-secondary"></span>
                  ) : (
                    <>
                      <i className="bi bi-person-fill fs-1 text-muted"></i>
                      <small className="text-muted">Add Photo</small>
                    </>
                  )}
                </label>
              )}
            </div>

            {preview && (
              <>
                <label style={styles.uploadButton}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    disabled={uploading}
                  />
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-camera-fill"></i>
                      Change Photo
                    </>
                  )}
                </label>
                <small className="d-block text-muted mt-1">Max 500KB</small>
              </>
            )}
          </div>

          {/* Name Field */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <i className="bi bi-person me-2"></i>
              Player Name
              <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              style={styles.input}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter player name"
              required
            />
          </div>

          {/* Captain Toggle */}
          <div style={styles.formGroup}>
            <div style={styles.switchContainer}>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="captainToggle"
                  checked={formData.isCaptain}
                  onChange={(e) => setFormData({ ...formData, isCaptain: e.target.checked })}
                  style={{ width: "48px", height: "24px", cursor: "pointer" }}
                />
              </div>
              <div>
                <label style={styles.switchLabel} htmlFor="captainToggle">
                  <i className="bi bi-star-fill text-warning me-2"></i>
                  Team Captain
                </label>
                <p style={styles.switchNote} className="mb-0">
                  Setting as captain will remove captain status from other players
                </p>
              </div>
            </div>
          </div>

          {/* Player Info Display */}
          {player && (
            <div
              style={{
                background: isDark ? "#2d2d44" : "#f8fafc",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <h6 className="mb-3" style={{ color: isDark ? "#a0a0a0" : "#64748b" }}>
                <i className="bi bi-info-circle me-2"></i>
                Player Details
              </h6>
              <div className="row">
                <div className="col-6">
                  <small style={{ color: isDark ? "#9ca3af" : "#6b7280" }} className="d-block">Role</small>
                  <span className="badge bg-info">{player.role || "N/A"}</span>
                </div>
                <div className="col-6">
                  <small style={{ color: isDark ? "#9ca3af" : "#6b7280" }} className="d-block">Jersey #</small>
                  <strong style={{ color: isDark ? "#e0e0e0" : "inherit" }}>{player.playerNumber || "N/A"}</strong>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-6">
                  <small style={{ color: isDark ? "#9ca3af" : "#6b7280" }} className="d-block">Base Price</small>
                  <strong style={{ color: isDark ? "#e0e0e0" : "inherit" }}>৳{player.price?.toLocaleString() || 0}</strong>
                </div>
                <div className="col-6">
                  <small style={{ color: isDark ? "#9ca3af" : "#6b7280" }} className="d-block">Sold Price</small>
                  <strong className="text-success">
                    ৳{player.soldPrice?.toLocaleString() || player.price?.toLocaleString() || 0}
                  </strong>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            style={{
              ...styles.submitButton,
              opacity: saving || uploading ? 0.7 : 1,
            }}
            disabled={saving || uploading}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle-fill"></i>
                Save Changes
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditTeamPlayer;
